import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check, Phone } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/25 focus:border-brass py-4 font-body text-white text-lg placeholder:text-white/40 focus:outline-none transition-colors";

/**
 * Signature editorial lead form. Minimal: Name + Phone required. Email + message optional.
 * Anchored at #speak-with-mike so the hero CTA scrolls straight to it.
 */
export const SpeakWithMike = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", { ...form, source: "speak_with_mike" });
      setDone(true);
      setForm({ name: "", phone: "", email: "", message: "" });
      toast.success("Thank you. Mike's team will be in touch.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="speak-with-mike" data-testid="speak-with-mike" className="relative bg-navy text-white overflow-hidden">
      {/* Editorial background numeral */}
      <span
        aria-hidden
        className="absolute -right-8 -top-16 font-heading text-[22rem] leading-none text-white/[0.03] select-none pointer-events-none"
      >
        05
      </span>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-36 grid lg:grid-cols-12 gap-14 lg:gap-24 items-start relative">
        <div className="lg:col-span-5">
          <Reveal><p className="overline">Chapter V · Direct Line</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light leading-[1.02] mt-6">
              Speak with <span className="italic text-brass">Mike</span>—<br />no gatekeepers.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-body text-white/70 text-lg leading-relaxed mt-8 max-w-md">
              Leave your name and number. Mike returns every call personally, usually the same business day.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <a href="tel:+16048312500" className="mt-10 inline-flex items-center gap-3 font-mono text-brass text-sm uppercase tracking-[0.2em] border-b border-brass/40 pb-1 hover:text-white hover:border-white transition-colors">
              <Phone size={14} /> 604-831-2500
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="speak-success"
              className="border-l-2 border-brass pl-8 py-6"
            >
              <div className="w-12 h-12 bg-brass text-white flex items-center justify-center"><Check size={24} /></div>
              <p className="overline mt-6">Message Received</p>
              <p className="font-heading text-3xl mt-3 leading-tight">Thank you. Mike's team will be in touch.</p>
              <button
                onClick={() => setDone(false)}
                data-testid="speak-send-another"
                className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-white/60 hover:text-brass border-b border-white/20 pb-1"
              >
                Send another
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="speak-form" className="space-y-2">
              <div className="grid sm:grid-cols-2 gap-x-8">
                <label className="block">
                  <span className="overline text-white/50">Name *</span>
                  <input
                    data-testid="speak-name"
                    className={inputCls}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Full name"
                  />
                </label>
                <label className="block">
                  <span className="overline text-white/50">Phone *</span>
                  <input
                    data-testid="speak-phone"
                    className={inputCls}
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="(604) 000-0000"
                  />
                </label>
              </div>
              <label className="block pt-3">
                <span className="overline text-white/50">Email (optional)</span>
                <input
                  data-testid="speak-email"
                  type="email"
                  className={inputCls}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@email.com"
                />
              </label>
              <label className="block pt-3">
                <span className="overline text-white/50">How can Mike help?</span>
                <textarea
                  data-testid="speak-message"
                  rows={2}
                  className={`${inputCls} resize-none`}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="A property, a court date, a question…"
                />
              </label>
              <button
                data-testid="speak-submit"
                type="submit"
                disabled={sending}
                className="mt-10 group inline-flex items-center gap-3 bg-brass text-white px-9 py-4 hover:bg-white hover:text-navy transition-colors duration-300 disabled:opacity-60 font-body text-sm tracking-wide"
              >
                {sending ? "Sending…" : "Request a Call"}
                <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
